import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  MagnifyingGlass,
  ArrowRight,
  MapPin,
  Clock,
  Users,
  ArrowUpRight,
  SlidersHorizontal,
} from "@phosphor-icons/react";
import type {
  Brand,
  EventSummary,
  Venue,
  Booking,
  Closure,
} from "../../shared/contracts";
import { api, useResource } from "../api";
import { useApp } from "../context";
import { categories, date, label } from "../i18n";
import {
  Empty,
  ErrorState,
  EventCard,
  Loading,
  PageHeading,
} from "../components/common";
import { VenueCalendar, type CalendarData } from "../components/VenueCalendar";
import { dateFilterValue } from "../lib/experience";
import { platformBrand } from "../../shared/platform-brand";
function useDiscoveryEvents(query: URLSearchParams) {
  const signature = query.toString();
  const [result, setResult] = useState<{
    data: {
      events: EventSummary[];
      total: number;
      nextOffset: number | null;
    } | null;
    error: Error | null;
    loading: boolean;
  }>({ data: null, error: null, loading: true });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setResult((old) => ({ ...old, loading: true, error: null }));
    const args = new URLSearchParams(signature);
    const pages = Math.min(100, Math.max(1, Number(args.get("page")) || 1));
    args.delete("page");
    args.set("limit", "24");
    Promise.all(
      Array.from({ length: pages }, (_, i) => {
        const p = new URLSearchParams(args);
        p.set("offset", String(i * 24));
        return api<{
          events: EventSummary[];
          total: number;
          nextOffset: number | null;
        }>("/events?" + p);
      }),
    )
      .then((pages) => {
        if (active)
          setResult({
            data: {
              events: [
                ...new Map(
                  pages.flatMap((p) => p.events).map((e) => [e.id, e]),
                ).values(),
              ],
              total: pages[0].total,
              nextOffset: pages[pages.length - 1].nextOffset,
            },
            error: null,
            loading: false,
          });
      })
      .catch((error) => {
        if (active) setResult({ data: null, error, loading: false });
      });
    return () => {
      active = false;
    };
  }, [signature, revision]);
  return { ...result, reload: () => setRevision((v) => v + 1) };
}
export function Home() {
  const { t, language } = useApp(),
    [params, setParams] = useSearchParams();
  const query = new URLSearchParams(params);
  if (!query.has("status")) query.set("status", "upcoming");
  const events = useDiscoveryEvents(query),
    brands = useResource<{ brands: Brand[] }>("/brands"),
    venue = useResource<{ venue: Venue }>("/venue");
  const [expanded, setExpanded] = useState(false);
  const filter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    next.delete("page");
    setParams(next);
  };
  return (
    <>
      <section className="discovery-intro">
        <div className="eyebrow">{platformBrand.domain} · TOKYO</div>
        <h1>
          <span>{t("新しいまちで、", "一座新城，")}</span>
          <span>{t("出会いの一歩を。", "从聚一次开始。")}</span>
        </h1>
        <p>
          {t(
            "東京から始まる、華人コミュニティの集まり。興味のある活動で、身近な人とつながりましょう。",
            "从东京出发，发现身边的华人活动。无论刚到一座城市，还是已经住了很久，都可以从一次相聚开始。",
          )}
        </p>
        <form
          className="search-bar"
          onSubmit={(e) => {
            e.preventDefault();
            filter("q", String(new FormData(e.currentTarget).get("q") || ""));
          }}
        >
          <MagnifyingGlass size={23} />
          <input
            name="q"
            key={params.get("q")}
            defaultValue={params.get("q") ?? ""}
            placeholder={t("イベントやブランドを検索", "搜索活动或品牌")}
            aria-label={t("イベントやブランドを検索", "搜索活动或品牌")}
          />
          <button className="button primary" type="submit">
            {t("検索", "搜索")}
            <ArrowRight size={18} />
          </button>
        </form>
      </section>
      <div className="filter-bar">
        <div className="category-pills">
          <button
            className={!params.get("category") ? "selected" : ""}
            onClick={() => filter("category", "")}
          >
            {t("すべて", "全部")}
          </button>
          {categories.map((c) => (
            <button
              key={c}
              className={params.get("category") === c ? "selected" : ""}
              onClick={() => filter("category", c)}
            >
              {label(c, language)}
            </button>
          ))}
        </div>
        <button
          className="filter-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          <SlidersHorizontal size={18} />
          {t("絞り込み", "筛选")}
        </button>
      </div>
      {expanded && (
        <div className="advanced-filters">
          <label>
            {t("表示", "活动状态")}
            <select
              value={params.get("status") || "upcoming"}
              onChange={(e) => filter("status", e.target.value)}
            >
              <option value="upcoming">{t("これから開催", "即将举办")}</option>
              <option value="poll">{t("日程調整中", "征集时间")}</option>
              <option value="history">{t("過去のイベント", "历史活动")}</option>
            </select>
          </label>
          <label>
            {t("使用言語", "活动语言")}
            <select
              value={params.get("language") || ""}
              onChange={(e) => filter("language", e.target.value)}
            >
              <option value="">{t("すべて", "全部")}</option>
              {["日本語", "中文", "日本語・中文", "English"].map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
          <label>
            {t("開催日（開始）", "开始日期")}
            <input
              type="date"
              value={dateFilterValue(params.get("from"))}
              onInput={(e) =>
                filter(
                  "from",
                  e.currentTarget.value
                    ? new Date(
                        e.currentTarget.value + "T00:00:00+09:00",
                      ).toISOString()
                    : "",
                )
              }
              onChange={(e) =>
                filter(
                  "from",
                  e.target.value
                    ? new Date(e.target.value + "T00:00:00+09:00").toISOString()
                    : "",
                )
              }
            />
          </label>
          <label>
            {t("開催日（終了）", "结束日期")}
            <input
              type="date"
              value={dateFilterValue(params.get("to"))}
              onInput={(e) =>
                filter(
                  "to",
                  e.currentTarget.value
                    ? new Date(
                        e.currentTarget.value + "T23:59:59+09:00",
                      ).toISOString()
                    : "",
                )
              }
              onChange={(e) =>
                filter(
                  "to",
                  e.target.value
                    ? new Date(e.target.value + "T23:59:59+09:00").toISOString()
                    : "",
                )
              }
            />
          </label>
          <button onClick={() => setParams({})}>{t("リセット", "重置")}</button>
        </div>
      )}
      <section className="discovery-brands">
        <div className="section-heading">
          <h2>{t("ブランドから探す", "认识这些品牌的活动")}</h2>
          <Link className="text-link" to="/brands">
            {t("すべてのブランド", "全部品牌")}
          </Link>
        </div>
        <div className="brand-discovery-strip">
          <button
            className={!params.get("brandId") ? "selected" : ""}
            onClick={() => filter("brandId", "")}
          >
            {t("すべてのブランド", "全部品牌")}
          </button>
          {brands.data?.brands.map((b) => (
            <div
              key={b.id}
              className={params.get("brandId") === b.id ? "selected" : ""}
            >
              <button onClick={() => filter("brandId", b.id)}>
                <img src={b.home?.logo ?? b.image} alt="" />
                <strong>{b.name}</strong>
              </button>
              <Link to={"/b/" + b.slug}>
                {t("ホーム・日程", "主页与日程")} ↗
              </Link>
            </div>
          ))}
        </div>
      </section>
      <div className="discovery-layout">
        <section>
          <div className="section-heading">
            <h2>
              {params.get("status") === "history"
                ? t("これまでの集まり", "历史活动")
                : t("これからの集まり", "接下来可以参加的活动")}
            </h2>
            <span className="muted">
              {events.data?.total ?? "—"} {t("件", "场")}
            </span>
          </div>
          {events.loading ? (
            <Loading />
          ) : events.error ? (
            <ErrorState error={events.error} retry={events.reload} />
          ) : events.data?.events.length ? (
            <>
              <div className="event-grid">
                {events.data.events.map((e) => (
                  <EventCard event={e} key={e.id} />
                ))}
              </div>
              {events.data.nextOffset !== null && (
                <button
                  className="load-more"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.set(
                      "page",
                      String((Number(params.get("page")) || 1) + 1),
                    );
                    setParams(next, { preventScrollReset: true });
                  }}
                >
                  {t("さらに24件を見る", "再看24场活动")}
                </button>
              )}
            </>
          ) : (
            <Empty>
              <h3>
                {t("条件に合うイベントがありません", "暂无符合条件的活动")}
              </h3>
              <p>
                {t(
                  "絞り込みを変えてみてください。",
                  "可以调整筛选条件再试一次。",
                )}
              </p>
              <button onClick={() => setParams({})}>
                {t("条件をクリア", "清除筛选")}
              </button>
            </Empty>
          )}
        </section>
        <aside className="venue-aside">
          <img
            src={venue.data?.venue.image ?? "/assets/venue.png"}
            alt={t("会場のイメージ", "场地示意图片")}
          />
          <div>
            <div className="eyebrow">OUR SPACE</div>
            <h2>{t("集まる場所も、ここに。", "相聚的地方，就在这里。")}</h2>
            <p>
              {t(
                "対話も、学びも、ワークショップも。東京の小さなコミュニティスペース。",
                "适合交流、学习与工作坊的东京社区空间。",
              )}
            </p>
            <p className="card-meta">
              <Users size={17} />
              {venue.data?.venue.capacity ?? 24}
              {t("名まで", "人容量")}
            </p>
            <p className="card-meta">
              <Clock size={17} />
              {venue.data?.venue.openHour ?? 9}:00–
              {venue.data?.venue.closeHour ?? 22}:00 · JST
            </p>
            <Link className="text-link" to="/venue">
              {t("会場・空き状況を見る", "查看场地与日历")}
              <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="aside-note">
            <h3>{t("あなたも、主催者に。", "你也可以成为主办方。")}</h3>
            <p>
              {t(
                "個人も、団体も。ブランドをつくり、初めての集まりをひらけます。",
                "无论个人还是团体，都可以创建品牌，举办自己的活动。",
              )}
            </p>
            <Link to="/host" className="button outline">
              {t("開催をはじめる", "开始主办")}
              <ArrowRight size={17} />
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
}
export function BrandsDirectory() {
  const { t } = useApp(),
    r = useResource<{ brands: Brand[] }>("/brands");
  return (
    <>
      <PageHeading
        eyebrow="COMMUNITIES"
        title={t("好きな集まりを、見つけよう。", "找到你喜欢的活动品牌。")}
      >
        {t(
          "団体にも、個人にも、活動を続ける場所を。",
          "让团体和个人，都有持续开展活动的空间。",
        )}
      </PageHeading>
      {r.loading ? (
        <Loading />
      ) : r.error ? (
        <ErrorState error={r.error} />
      ) : (
        <div className="brand-grid">
          {r.data?.brands.map((b) => (
            <Link className="brand-card" key={b.id} to={"/brands/" + b.id}>
              <img src={b.image} alt="" />
              <div>
                <h2>{b.name}</h2>
                <p>{b.description}</p>
                <span className="text-link">
                  {t("ブランドを見る", "查看品牌")}
                  <ArrowRight size={18} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
export function BrandDetail() {
  const { id } = useParams(),
    { t, user, toast, errorText } = useApp();
  const r = useResource<{
      brand: Brand;
      events: EventSummary[];
      follow: { follow: boolean; subscribe: boolean } | null;
      canManage: boolean;
    }>("/brands/" + id),
    [tab, setTab] = useState("upcoming"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  if (r.loading) return <Loading />;
  if (r.error || !r.data)
    return <ErrorState error={r.error} retry={r.reload} />;
  const { brand, events, follow, canManage } = r.data;
  const list = events.filter((e) => {
    const ended =
      e.status === "cancelled" || (e.end && Date.parse(e.end) < Date.now());
    return tab === "history" ? ended : !ended;
  });
  async function change(f: boolean, s: boolean) {
    setBusy(true);
    setError("");
    try {
      await api("/brands/" + id + "/follow", "POST", {
        follow: f,
        subscribe: s,
      });
      r.reload();
      toast(t("フォロー設定を更新しました", "已更新关注设置"));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <div className="brand-hero">
        <img src={brand.image} alt="" />
        <div>
          <div className="eyebrow">COMMUNITY BRAND</div>
          <h1>{brand.name}</h1>
          <p className="preserve">{brand.description}</p>
          {canManage && (
            <Link className="text-link" to={"/host/brands/" + id}>
              {t("ブランドを管理", "管理品牌")}
              <ArrowRight size={18} />
            </Link>
          )}
          <div className="follow-actions">
            {user ? (
              <>
                <button
                  className={
                    follow?.follow ? "button outline" : "button primary"
                  }
                  disabled={busy}
                  onClick={() => change(!follow?.follow, !!follow?.subscribe)}
                >
                  {follow?.follow
                    ? t("フォロー中 · 解除", "已关注 · 取消")
                    : t("フォローする", "关注品牌")}
                </button>
                {follow?.follow && (
                  <label className="checkbox-line">
                    <input
                      type="checkbox"
                      checked={!!follow.subscribe}
                      disabled={busy}
                      onChange={(e) => change(true, e.target.checked)}
                    />
                    {t(
                      "新しいイベントをメールで受け取る",
                      "通过邮件接收新活动",
                    )}
                  </label>
                )}
              </>
            ) : (
              <Link
                className="button primary"
                to={"/login?return=" + encodeURIComponent("/brands/" + id)}
              >
                {t("ログインしてフォロー", "登录并关注")}
              </Link>
            )}
          </div>
          {error && (
            <p role="alert" className="error-text">
              {error}
            </p>
          )}
        </div>
      </div>
      <div className="tabs">
        <button
          className={tab === "upcoming" ? "active" : ""}
          onClick={() => setTab("upcoming")}
        >
          {t("開催予定", "即将举办")}
        </button>
        <button
          className={tab === "history" ? "active" : ""}
          onClick={() => setTab("history")}
        >
          {t("これまでの活動", "活动历史")}
        </button>
      </div>
      {list.length ? (
        <div className="event-grid full-grid">
          {list.map((e) => (
            <EventCard key={e.id} event={e} />
          ))}
        </div>
      ) : (
        <Empty>
          {t("この一覧にイベントはまだありません。", "此列表暂时没有活动。")}
        </Empty>
      )}
    </>
  );
}
export function VenuePage() {
  const { t } = useApp(),
    [params, setParams] = useSearchParams(),
    venueId = params.get("venueId") ?? "main",
    inventory = useResource<{ venues: Venue[] }>("/venues"),
    r = useResource<CalendarData>(
      `/venue/calendar?venueId=${encodeURIComponent(venueId)}`,
    );
  if (r.loading) return <Loading />;
  if (r.error || !r.data) return <ErrorState error={r.error} />;
  const { venue } = r.data;
  return (
    <>
      <PageHeading
        eyebrow="OUR COMMUNITY SPACE"
        title={t("ここから、集まりがはじまる。", "在这里，让相聚发生。")}
      >
        {t(
          "ログインして会場を予約できます。ブランドへの所属は不要です。",
          "登录后即可申请预约场地，无需品牌身份。",
        )}
      </PageHeading>
      <label className="venue-public-selector">
        {t("会場を選ぶ", "选择场地")}
        <select
          value={venueId}
          onChange={(e) =>
            setParams({
              venueId: e.target.value,
              ...(params.get("date") ? { date: params.get("date")! } : {}),
            })
          }
        >
          {!inventory.data?.venues.some((v) => v.id === venueId) && (
            <option value={venueId}>{venue.name}</option>
          )}
          {inventory.data?.venues.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </label>
      {venue.active === false && (
        <p className="notice">
          {t(
            "この会場は現在、新しい予約を停止しています。",
            "此场地已暂停接受新预约，日历保留历史信息。",
          )}
        </p>
      )}
      <div className="venue-hero">
        <img
          src={venue.image}
          alt={t("会場イメージ（サンプル）", "场地示意图（样例）")}
        />
        <div>
          <h2>{venue.name}</h2>
          <p className="preserve">{venue.description}</p>
          <p className="card-meta">
            <MapPin size={20} />
            {venue.address}
          </p>
          <p className="card-meta">
            <Users size={20} />
            {t(`定員 ${venue.capacity} 名`, `容量 ${venue.capacity} 人`)}
          </p>
          <p className="card-meta">
            <Clock size={20} />
            {venue.openHour}:00–{venue.closeHour}:00 (JST)
          </p>
          <p className="muted">
            {t(
              `選んだ利用時間内で準備・片付けを行ってください。予約間の最小間隔は ${venue.gapMinutes} 分です。`,
              `准备、活动和收拾均须在所选利用时间内完成。预约之间最少间隔 ${venue.gapMinutes} 分钟。`,
            )}
          </p>
          {venue.active !== false && (
            <Link
              className="button primary"
              to={
                "/venue/reservations/new?" +
                new URLSearchParams({
                  venueId,
                  ...(params.get("date") ? { date: params.get("date")! } : {}),
                })
              }
            >
              {t("この会場を予約", "预约此场地")}
              <ArrowRight size={18} />
            </Link>
          )}
          <p>
            <Link to="/me/venue-reservations">
              {t("マイ会場予約", "我的场地预约")}
            </Link>
          </p>
          <p className="muted">
            {t(
              "会場利用料は運営への確認が必要です。イベント参加費とは別にご案内します。",
              "场地使用费须向运营方确认，与活动参加费分别说明。",
            )}
          </p>
        </div>
      </div>
      <h2>{t("会場の利用状況", "场地日历")}</h2>
      <p className="muted">
        {t(
          "表示は確定済み予約と利用停止です。候補日時・申請中の日時は会場を確保していません。空き時間の利用は運営承認後に確定します。",
          "显示已确认的预约及停用时段。候选时间、待审批申请不会占用场地，空闲时段须经管理员批准才算预约成功。",
        )}
      </p>
      <VenueCalendar
        data={r.data}
        selectedDate={params.get("date") ?? undefined}
        onSelectDate={(day) =>
          setParams({ venueId, date: day }, { replace: true })
        }
      />
    </>
  );
}
export function Guide() {
  const { t } = useApp();
  return (
    <div className="prose">
      <PageHeading title={t("ご利用ガイド", "使用指南")} />
      <h2>{t("参加する", "参加活动")}</h2>
      <p>
        {t(
          "メールアドレスで認証し、開催確定のイベントへ申し込みます。定員に達した場合はキャンセル待ちになります。日程調整中のイベントは参加可能な候補を選び、最終日程と会場の確定後に届く案内で参加を確認します。",
          "通过邮箱验证码登录，选择已确认举办的活动报名。满员时进入候补。征集时间的活动请勾选可参加的时间，主办方确定时间且场地审批通过后，收到邀请再确认参加。",
        )}
      </p>
      <h2>{t("開催する", "主办活动")}</h2>
      <p>
        {t(
          "ブランドを作成し、日時が決まったイベント、または2〜5件の候補をもつイベントを作成します。会場への申請は運営承認後に予約確定となります。",
          "创建品牌后可以发起固定时间活动，或提供 2–5 个候选时间。场地申请须经运营管理员批准后才预约成功。",
        )}
      </p>
      <h2>{t("参加費と通知", "费用与通知")}</h2>
      <p>
        {t(
          "現在はオンライン決済に対応していません。参加費は主催者の説明に従ってお支払いください。登録、確認依頼、変更、中止などの重要な通知はメールとサイト内のお知らせに保存されます。",
          "当前不提供在线支付。请按主办方说明线下付款。报名、确认邀请、变更、取消等必要通知将通过邮件通知，并保存在站内。",
        )}
      </p>
      <h2>{t("個人情報と公開前の確認", "隐私及上线前确认")}</h2>
      <p>
        {t(
          "参加者のメールアドレスはそのイベントの権限を持つ主催者だけが閲覧できます。ブランドの新規イベント通知は任意です。本環境は実装レビュー用です。公開前に運営者情報、実際の住所、問い合わせ窓口、個人情報保護方針と利用規約を確定する必要があります。",
          "报名者邮箱仅对有权限的对应活动主办方开放。品牌新活动通知可自由订阅。本环境供实现评审，上线前需填入运营者信息、真实地址、联系渠道，并确认正式隐私政策和使用条款。",
        )}
      </p>
    </div>
  );
}
